import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import RecruiterLayout from '../components/RecruiterLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { MapPin, Star, MoreHorizontal, Check, X, FileCheck, MailX, Search } from 'lucide-react';
import AsyncInterviewModal from '../components/AsyncInterviewModal';
import BulkEmailModal from '../components/BulkEmailModal';

const PIPELINE_STAGES = [
  { key: 'all', label: 'All Applications', bg: 'bg-pink-400', border: 'border-pink-500', hover: 'hover:bg-pink-50', borderBottom: 'border-pink-400' },
  { key: 'screening', label: 'Screening', bg: 'bg-purple-400', border: 'border-purple-500', hover: 'hover:bg-purple-50', borderBottom: 'border-purple-400', dbStatus: 'reviewing' },
  { key: 'interview_recruiter', label: 'Interview Recruiter', bg: 'bg-sky-400', border: 'border-sky-500', hover: 'hover:bg-sky-50', borderBottom: 'border-sky-400', dbStatus: 'shortlisted' },
  { key: 'interview_hiring', label: 'Interview Hiring', bg: 'bg-blue-400', border: 'border-blue-500', hover: 'hover:bg-blue-50', borderBottom: 'border-blue-400', dbStatus: 'interviewed' },
  { key: 'onsite_interview', label: 'Onsite Interview', bg: 'bg-blue-600', border: 'border-blue-700', hover: 'hover:bg-blue-50', borderBottom: 'border-blue-600', dbStatus: 'onsite_interview' },
  { key: 'decision', label: 'Decision', bg: 'bg-amber-400', border: 'border-amber-500', hover: 'hover:bg-amber-50', borderBottom: 'border-amber-400', dbStatus: 'decision' },
  { key: 'contract', label: 'Contract', bg: 'bg-green-500', border: 'border-green-600', hover: 'hover:bg-green-50', borderBottom: 'border-green-500', dbStatus: 'offered' },
  { key: 'rejected', label: 'Rejected', bg: 'bg-orange-400', border: 'border-orange-500', hover: 'hover:bg-orange-50', borderBottom: 'border-orange-400', dbStatus: 'rejected' },
  { key: 'withdrawn', label: 'Withdrawn', bg: 'bg-red-500', border: 'border-red-600', hover: 'hover:bg-red-50', borderBottom: 'border-red-500', dbStatus: 'withdrawn' },
];

const SCORE_BUCKETS = [
  { key: 'excellent', label: 'Excellent', percent: '70–100%', grade: 'A', min: 70, max: 100, dot: 'bg-green-500' },
  { key: 'fair',      label: 'Fair',      percent: '40–69%',  grade: 'B', min: 40, max: 69,  dot: 'bg-amber-400' },
  { key: 'poor',      label: 'Poor',      percent: '0–39%',   grade: 'C', min: 0,  max: 39,  dot: 'bg-red-500' },
];

const MyCandidates = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile } = useAuth();
  const [candidates, setCandidates] = useState([]);
  const [filteredCandidates, setFilteredCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeStage, setActiveStage] = useState(location.state?.stage || 'all');

  // Filters
  const [selectedPositions, setSelectedPositions] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [selectedScoreBuckets, setSelectedScoreBuckets] = useState([]);
  const [nameSearch, setNameSearch] = useState('');
  const [positionSearchText, setPositionSearchText] = useState('');
  const [showPositionDropdown, setShowPositionDropdown] = useState(false);
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [showScoreDropdown, setShowScoreDropdown] = useState(false);
  // Async interview modal state
  const [asyncModalCandidate, setAsyncModalCandidate] = useState(null);

  // Multi-select & bulk actions
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkEmailType, setBulkEmailType] = useState(null); // 'contract' | 'rejection' | null
  const [showBulkEmail, setShowBulkEmail] = useState(false);
  const [showBulkMove, setShowBulkMove] = useState(false);
  const [bulkMoving, setBulkMoving] = useState(false);

  // Drag & drop
  const [draggedId, setDraggedId] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);

  // Refs for click-outside
  const positionRef = useRef(null);
  const cityRef = useRef(null);
  const scoreRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (positionRef.current && !positionRef.current.contains(e.target)) setShowPositionDropdown(false);
      if (cityRef.current && !cityRef.current.contains(e.target)) setShowCityDropdown(false);
      if (scoreRef.current && !scoreRef.current.contains(e.target)) setShowScoreDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (userProfile?.organizationId) fetchCandidates();
  }, [userProfile]);

  // Pre-select candidates passed from external sources (e.g. lasso selection in dashboard)
  useEffect(() => {
    const incoming = location.state?.preselectedIds;
    if (Array.isArray(incoming) && incoming.length > 0) {
      setSelectedIds(incoming);
      // Clear state from history so refresh doesn't re-trigger
      try { window.history.replaceState({}, document.title); } catch { /* noop */ }
    }
  }, [location.state?.preselectedIds]);

  useEffect(() => {
    applyFilters();
  }, [candidates, activeStage, selectedPositions, selectedCities, selectedScoreBuckets, nameSearch]);

  const fetchCandidates = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/positions/organization/${userProfile.organizationId}/candidates`
      );
      if (response.data.success) setCandidates(response.data.data || []);
    } catch (error) {
      console.error('Error fetching candidates:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...candidates];

    // Stage filter
    if (activeStage !== 'all') {
      filtered = filtered.filter(c => {
        const status = c.applicationStatus || 'pending';
        const stage = PIPELINE_STAGES.find(s => s.key === activeStage);
        if (!stage?.dbStatus) return true;
        if (stage.key === 'contract') return status === 'offered' || status === 'accepted';
        return status === stage.dbStatus;
      });
    }

    // Position filter
    if (selectedPositions.length > 0) {
      filtered = filtered.filter(c => selectedPositions.includes(c.appliedPosition || c.positionTitle));
    }

    // City filter (from position location)
    if (selectedCities.length > 0) {
      filtered = filtered.filter(c => selectedCities.includes(c.positionLocation));
    }

    // Match score bucket filter
    if (selectedScoreBuckets.length > 0) {
      filtered = filtered.filter(c => {
        if (c.matchScore == null) return false; // unscored candidates excluded when score filter active
        return selectedScoreBuckets.some(key => {
          const b = SCORE_BUCKETS.find(s => s.key === key);
          return b && c.matchScore >= b.min && c.matchScore <= b.max;
        });
      });
    }

    // Name search
    if (nameSearch.trim()) {
      const q = nameSearch.toLowerCase();
      filtered = filtered.filter(c => 
        `${c.firstName} ${c.lastName}`.toLowerCase().includes(q)
      );
    }

    setFilteredCandidates(filtered);
  };

  // Derived filter options - contextual based on current filters
  const getAvailablePositions = () => {
    let pool = [...candidates];
    // Apply stage filter to pool
    if (activeStage !== 'all') {
      pool = pool.filter(c => {
        const status = c.applicationStatus || 'pending';
        const stage = PIPELINE_STAGES.find(s => s.key === activeStage);
        if (!stage?.dbStatus) return true;
        if (stage.key === 'contract') return status === 'offered' || status === 'accepted';
        return status === stage.dbStatus;
      });
    }
    // If cities selected, narrow positions to those matching cities
    if (selectedCities.length > 0) {
      pool = pool.filter(c => selectedCities.includes(c.positionLocation));
    }
    return [...new Set(pool.map(c => c.appliedPosition || c.positionTitle).filter(Boolean))];
  };

  const getAvailableCities = () => {
    let pool = [...candidates];
    // Apply stage filter
    if (activeStage !== 'all') {
      pool = pool.filter(c => {
        const status = c.applicationStatus || 'pending';
        const stage = PIPELINE_STAGES.find(s => s.key === activeStage);
        if (!stage?.dbStatus) return true;
        if (stage.key === 'contract') return status === 'offered' || status === 'accepted';
        return status === stage.dbStatus;
      });
    }
    // If positions selected, narrow cities to those matching positions
    if (selectedPositions.length > 0) {
      pool = pool.filter(c => selectedPositions.includes(c.appliedPosition || c.positionTitle));
    }
    return [...new Set(pool.map(c => c.positionLocation).filter(Boolean))];
  };

  const availablePositions = getAvailablePositions();
  const availableCities = getAvailableCities();

  // Stage counts
  const getStageCounts = () => {
    const counts = { all: candidates.length };
    PIPELINE_STAGES.forEach(s => {
      if (s.key === 'all') return;
      if (s.key === 'contract') {
        counts[s.key] = candidates.filter(c => c.applicationStatus === 'offered' || c.applicationStatus === 'accepted').length;
      } else if (s.dbStatus) {
        counts[s.key] = candidates.filter(c => c.applicationStatus === s.dbStatus).length;
      }
    });
    return counts;
  };
  const stageCounts = getStageCounts();

  // Selection
  const toggleSelect = (id, e) => {
    e.stopPropagation();
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const bulkMoveToStatus = async (targetStage) => {
    const stage = PIPELINE_STAGES.find(s => s.key === targetStage);
    if (!stage?.dbStatus || selectedIds.length === 0) return;
    setBulkMoving(true);
    try {
      await Promise.all(selectedIds.map(id =>
        axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/applications/${id}/status`, { status: stage.dbStatus })
      ));
      setCandidates(prev => prev.map(c =>
        selectedIds.includes(c.applicationId) ? { ...c, applicationStatus: stage.dbStatus, status: stage.dbStatus } : c
      ));
      setSelectedIds([]);
      setShowBulkMove(false);
    } catch (err) {
      console.error('Bulk move error:', err);
    } finally {
      setBulkMoving(false);
    }
  };

  // Drag & drop
  const handleDragStart = (e, id) => { setDraggedId(id); e.dataTransfer.effectAllowed = 'move'; };
  const handleDragOver = (e, key) => { e.preventDefault(); if (key !== 'all') setDragOverStage(key); };
  const handleDragLeave = () => setDragOverStage(null);
  const handleDrop = async (e, key) => {
    e.preventDefault();
    setDragOverStage(null);
    if (!draggedId || key === 'all') return;
    const stage = PIPELINE_STAGES.find(s => s.key === key);
    if (!stage?.dbStatus) return;
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/applications/${draggedId}/status`, { status: stage.dbStatus });
      setCandidates(prev => prev.map(c =>
        c.applicationId === draggedId ? { ...c, applicationStatus: stage.dbStatus, status: stage.dbStatus } : c
      ));
    } catch (err) { console.error('Drop error:', err); }
    setDraggedId(null);
  };

  const togglePosition = (pos) => setSelectedPositions(prev => prev.includes(pos) ? prev.filter(p => p !== pos) : [...prev, pos]);
  const toggleCity = (city) => setSelectedCities(prev => prev.includes(city) ? prev.filter(c => c !== city) : [...prev, city]);
  const toggleScoreBucket = (key) => setSelectedScoreBuckets(prev => prev.includes(key) ? prev.filter(b => b !== key) : [...prev, key]);

  if (loading) {
    return (
      <RecruiterLayout>
        <div className="max-w-7xl mx-auto p-4 lg:p-6"><p className="text-center text-gray-600">Loading candidates...</p></div>
      </RecruiterLayout>
    );
  }

  return (
    <RecruiterLayout>
      <div className="max-w-7xl mx-auto p-4 lg:p-6">
        <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight mb-4 lg:mb-6">Candidates</h1>
        
        <Tabs defaultValue="in-progress" className="w-full">
          <TabsList className="grid w-fit grid-cols-2 mb-4 lg:mb-6">
            <TabsTrigger value="in-progress">In progress</TabsTrigger>
            <TabsTrigger value="archive">Archive</TabsTrigger>
          </TabsList>
          
          <TabsContent value="in-progress" className="mt-0">
            {/* Pipeline Stage Counts — also drop targets */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 mb-6">
              <div className="grid grid-cols-2 md:grid-cols-5 lg:grid-cols-9 gap-4 text-center">
                {PIPELINE_STAGES.map(stage => (
                  <div
                    key={stage.key}
                    onClick={() => setActiveStage(stage.key)}
                    onDragOver={(e) => handleDragOver(e, stage.key)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, stage.key)}
                    className={`cursor-pointer rounded-lg p-3 transition-all ${
                      dragOverStage === stage.key ? `ring-2 ring-blue-500 scale-105 ${stage.bg} text-white` :
                      activeStage === stage.key
                        ? `${stage.bg} text-white border-2 ${stage.border} shadow-md`
                        : `border-b-2 ${stage.borderBottom} ${stage.hover}`
                    }`}
                    data-testid={`stage-${stage.key}`}
                  >
                    <div className="text-2xl font-bold">{stageCounts[stage.key] || 0}</div>
                    <div className={`text-xs ${activeStage === stage.key || dragOverStage === stage.key ? 'text-white' : 'text-gray-600'}`}>
                      {stage.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Search Bar — matches /jobs seeker search styling */}
            <div className="mb-6">
              <div className="flex flex-col sm:flex-row items-stretch bg-white rounded-full shadow-sm border border-slate-200 focus-within:border-blue-500 transition-colors overflow-hidden">
                <div className="flex items-center gap-2 flex-1 min-w-0 px-5 py-3.5">
                  <Search className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  <input
                    type="text"
                    placeholder="Search by name..."
                    value={nameSearch}
                    onChange={(e) => setNameSearch(e.target.value)}
                    className="flex-1 bg-transparent outline-none text-sm text-gray-900 placeholder:text-gray-400 min-w-0"
                    data-testid="filter-name"
                  />
                  {nameSearch && (
                    <button type="button" onClick={() => setNameSearch('')} className="p-1 text-gray-400 hover:text-gray-600" aria-label="Clear search">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => { setSelectedPositions([]); setSelectedCities([]); setSelectedScoreBuckets([]); setNameSearch(''); setPositionSearchText(''); }}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm px-8 py-3.5 sm:m-2 rounded-full transition-colors"
                  data-testid="candidates-reset-filters"
                >
                  Reset filters
                </button>
              </div>
              {/* Active filter tags */}
              {(selectedPositions.length > 0 || selectedCities.length > 0 || selectedScoreBuckets.length > 0) && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {selectedPositions.map(pos => (
                    <span key={pos} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">
                      {pos}<button onClick={() => togglePosition(pos)}><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                  {selectedCities.map(city => (
                    <span key={city} className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-50 text-green-700 rounded text-xs">
                      {city}<button onClick={() => toggleCity(city)}><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                  {selectedScoreBuckets.map(key => {
                    const b = SCORE_BUCKETS.find(s => s.key === key);
                    if (!b) return null;
                    return (
                      <span key={key} className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 rounded text-xs">
                        <span className={`w-1.5 h-1.5 rounded-full ${b.dot}`} />
                        <span className="font-semibold">{b.percent}</span>
                        <button onClick={() => toggleScoreBucket(key)}><X className="w-3 h-3" /></button>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Bulk Action Bar */}
            {selectedIds.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-center justify-between" data-testid="bulk-action-bar">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-blue-800">{selectedIds.length} selected</span>
                  {filteredCandidates.length > 0 && selectedIds.length < filteredCandidates.length && (
                    <button
                      onClick={() => setSelectedIds(filteredCandidates.map(c => c.applicationId))}
                      className="text-xs text-blue-600 hover:underline"
                      data-testid="select-all-link"
                    >
                      Select all ({filteredCandidates.length})
                    </button>
                  )}
                  <button onClick={() => setSelectedIds([])} className="text-xs text-blue-600 hover:underline" data-testid="deselect-all-link">Deselect all</button>
                </div>
                <div className="flex items-center gap-2">
                  {/* Bulk Email — only on Contract / Rejected stages */}
                  {(activeStage === 'contract' || activeStage === 'rejected') && (
                    <div className="relative">
                      <Button
                        size="sm"
                        onClick={() => setShowBulkEmail(!showBulkEmail)}
                        className="bg-blue-600 hover:bg-blue-700 text-white text-xs"
                        data-testid="bulk-email-btn"
                      >
                        Bulk Email
                      </Button>
                      {showBulkEmail && (
                        <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 min-w-[210px] py-1">
                          {activeStage === 'contract' && (
                            <button
                              onClick={() => { setBulkEmailType('contract'); setShowBulkEmail(false); }}
                              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2"
                              data-testid="bulk-email-contract-option"
                            >
                              <FileCheck className="w-3.5 h-3.5 text-green-600" />
                              Send contract email
                            </button>
                          )}
                          {activeStage === 'rejected' && (
                            <button
                              onClick={() => { setBulkEmailType('rejection'); setShowBulkEmail(false); }}
                              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2"
                              data-testid="bulk-email-rejection-option"
                            >
                              <MailX className="w-3.5 h-3.5 text-gray-500" />
                              Send rejection email
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {/* Move to... dropdown */}
                  <div className="relative">
                    <Button size="sm" onClick={() => setShowBulkMove(!showBulkMove)} className="bg-blue-600 hover:bg-blue-700 text-white text-xs" data-testid="bulk-move-btn">
                      {bulkMoving ? 'Moving...' : 'Move to...'}
                    </Button>
                    {showBulkMove && (
                      <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 min-w-[180px] py-1">
                        {PIPELINE_STAGES.filter(s => s.key !== 'all').map(stage => (
                          <button key={stage.key} onClick={() => bulkMoveToStatus(stage.key)} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2">
                            <div className={`w-2.5 h-2.5 rounded-full ${stage.bg}`}></div>
                            {stage.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Main Content: Sidebar + Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Sidebar Filters */}
              <div className="lg:col-span-1">
                <Card className="border-slate-200 shadow-sm">
                  <CardContent className="p-4 space-y-6">
                    {/* Position (multi-select with type-to-search) */}
                    <div ref={positionRef}>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">Position</label>
                      <div className="relative">
                        <input
                          type="text"
                          value={positionSearchText}
                          onChange={(e) => { setPositionSearchText(e.target.value); setShowPositionDropdown(true); }}
                          onFocus={() => { setShowPositionDropdown(true); setShowCityDropdown(false); }}
                          placeholder="Search positions..."
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-400"
                          data-testid="filter-position"
                        />
                        {showPositionDropdown && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-[200px] overflow-y-auto py-1">
                            {availablePositions
                              .filter(pos => pos.toLowerCase().includes(positionSearchText.toLowerCase()))
                              .map(pos => (
                              <button key={pos} onClick={() => { togglePosition(pos); setPositionSearchText(''); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50">
                                <div className={`w-4 h-4 rounded border flex items-center justify-center ${selectedPositions.includes(pos) ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`}>
                                  {selectedPositions.includes(pos) && <Check className="w-3 h-3 text-white" />}
                                </div>
                                <span className="truncate">{pos}</span>
                              </button>
                            ))}
                            {availablePositions.filter(pos => pos.toLowerCase().includes(positionSearchText.toLowerCase())).length === 0 && (
                              <p className="px-3 py-2 text-xs text-gray-400">No matching positions</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* City (multi-select from position locations) */}
                    <div ref={cityRef}>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">City</label>
                      <div className="relative">
                        <button
                          onClick={() => { setShowCityDropdown(!showCityDropdown); setShowPositionDropdown(false); }}
                          className="w-full flex items-center justify-between px-3 py-2 border border-gray-200 rounded-lg text-sm text-left hover:border-blue-400"
                          data-testid="filter-city"
                        >
                          <span className="text-gray-500">{selectedCities.length ? `${selectedCities.length} selected` : 'All cities'}</span>
                        </button>
                        {showCityDropdown && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-[200px] overflow-y-auto py-1">
                            {availableCities.length > 0 ? availableCities.map(city => (
                              <button key={city} onClick={() => toggleCity(city)} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50">
                                <div className={`w-4 h-4 rounded border flex items-center justify-center ${selectedCities.includes(city) ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`}>
                                  {selectedCities.includes(city) && <Check className="w-3 h-3 text-white" />}
                                </div>
                                <span className="truncate">{city}</span>
                              </button>
                            )) : (
                              <p className="px-3 py-2 text-xs text-gray-400">No cities available</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Match Score (multi-select buckets) */}
                    <div ref={scoreRef}>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">Match Score</label>
                      <div className="relative">
                        <button
                          onClick={() => { setShowScoreDropdown(!showScoreDropdown); setShowPositionDropdown(false); setShowCityDropdown(false); }}
                          className="w-full flex items-center justify-between px-3 py-2 border border-gray-200 rounded-lg text-sm text-left hover:border-blue-400"
                          data-testid="filter-score"
                        >
                          <span className={selectedScoreBuckets.length ? 'text-gray-800 font-medium' : 'text-gray-500'}>
                            {selectedScoreBuckets.length
                              ? selectedScoreBuckets.map(k => SCORE_BUCKETS.find(s => s.key === k)?.percent).filter(Boolean).join(', ')
                              : 'All scores'}
                          </span>
                        </button>
                        {showScoreDropdown && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-[220px] overflow-y-auto py-1">
                            {SCORE_BUCKETS.map(b => (
                              <button
                                key={b.key}
                                onClick={() => toggleScoreBucket(b.key)}
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50"
                                data-testid={`filter-score-${b.key}`}
                              >
                                <div className={`w-4 h-4 rounded border flex items-center justify-center ${selectedScoreBuckets.includes(b.key) ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`}>
                                  {selectedScoreBuckets.includes(b.key) && <Check className="w-3 h-3 text-white" />}
                                </div>
                                <span className={`w-2 h-2 rounded-full ${b.dot}`} />
                                <span className="flex-1 font-semibold text-gray-800">{b.percent}</span>
                                <span className="text-[10px] text-gray-400">{b.label}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Candidates List */}
              <div className="lg:col-span-3 space-y-4">
                {/* Persistent Select-All header (always visible when there are candidates) */}
                {filteredCandidates.length > 0 && (
                  <div className="flex items-center justify-between px-1" data-testid="select-all-header">
                    <button
                      onClick={() => {
                        const allIds = filteredCandidates.map(c => c.applicationId);
                        const allSelected = allIds.every(id => selectedIds.includes(id));
                        setSelectedIds(allSelected ? [] : allIds);
                      }}
                      className="flex items-center gap-2 text-sm text-gray-600 hover:text-blue-600 transition-colors"
                      data-testid="toggle-select-all"
                    >
                      <span className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                        filteredCandidates.length > 0 && filteredCandidates.every(c => selectedIds.includes(c.applicationId))
                          ? 'bg-blue-600 border-blue-600 text-white'
                          : 'border-gray-300 hover:border-blue-400'
                      }`}>
                        {filteredCandidates.every(c => selectedIds.includes(c.applicationId)) && <Check className="w-3 h-3" />}
                      </span>
                      <span>
                        {filteredCandidates.every(c => selectedIds.includes(c.applicationId))
                          ? `Deselect all (${filteredCandidates.length})`
                          : `Select all (${filteredCandidates.length})`}
                      </span>
                    </button>
                    <span className="text-xs text-gray-400">{filteredCandidates.length} {filteredCandidates.length === 1 ? 'candidate' : 'candidates'}</span>
                  </div>
                )}
                {filteredCandidates.length === 0 ? (
                  <Card>
                    <CardContent className="p-12 text-center">
                      <p className="text-gray-600">No candidates found</p>
                      <p className="text-sm text-gray-500 mt-2">
                        {candidates.length === 0 ? "No one has applied to your positions yet" : "Try adjusting your filters"}
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  filteredCandidates.map((candidate) => (
                    <Card 
                      key={candidate.applicationId}
                      className={`border-slate-200 shadow-sm hover:shadow-md hover:border-blue-300 transition-all cursor-pointer ${
                        selectedIds.includes(candidate.applicationId) ? 'ring-2 ring-blue-400 bg-blue-50/30' : ''
                      } ${draggedId === candidate.applicationId ? 'opacity-50' : ''}`}
                      data-testid={`candidate-card-${candidate.applicationId}`}
                      draggable
                      onDragStart={(e) => handleDragStart(e, candidate.applicationId)}
                      onDragEnd={() => setDraggedId(null)}
                      onClick={() => navigate(`/candidates/${candidate.applicationId}`, { 
                        state: { candidate, applicationId: candidate.applicationId, positionId: candidate.appliedPositionId } 
                      })}
                    >
                      <CardContent className="p-4 lg:p-6">
                        <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
                          <div className="flex items-center gap-4 flex-1 min-w-0">
                            {/* Checkbox */}
                            <button
                              onClick={(e) => toggleSelect(candidate.applicationId, e)}
                              className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                                selectedIds.includes(candidate.applicationId)
                                  ? 'bg-blue-600 border-blue-600 text-white'
                                  : 'border-gray-300 hover:border-blue-400'
                              }`}
                              data-testid={`select-${candidate.applicationId}`}
                            >
                              {selectedIds.includes(candidate.applicationId) && <Check className="w-3 h-3" />}
                            </button>

                            {/* Avatar */}
                            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                              <span className="text-blue-600 font-semibold text-lg">
                                {candidate.firstName?.[0]}{candidate.lastName?.[0]}
                              </span>
                            </div>

                            {/* Info */}
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-blue-600 mb-1 truncate">
                                {candidate.firstName} {candidate.lastName}
                              </h3>
                              <p className="text-sm text-gray-600 mb-2 truncate">
                                {candidate.currentCompany || candidate.appliedPosition}
                              </p>
                              <div className="flex flex-wrap gap-1 mb-2">
                                {candidate.skills?.slice(0, 5).map((skill, idx) => (
                                  <Badge key={`${skill}-${idx}`} variant="secondary" className="text-xs">
                                    {skill}
                                  </Badge>
                                ))}
                                {candidate.skills?.length > 5 && (
                                  <Badge variant="secondary" className="text-xs">+{candidate.skills.length - 5}</Badge>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex sm:flex-col items-end justify-between w-full sm:w-auto gap-4">
                            <div className="flex items-center gap-2 sm:mb-4 order-2 sm:order-1">
                              <button className="text-gray-400 hover:text-gray-600 p-1" onClick={(e) => e.stopPropagation()}>
                                <MoreHorizontal className="w-4 h-4" />
                              </button>
                              <button className="text-gray-400 hover:text-yellow-500 p-1" onClick={(e) => e.stopPropagation()}>
                                <Star className="w-4 h-4" />
                              </button>
                            </div>
                            <div className="text-sm text-gray-600 text-left sm:text-right order-1 sm:order-2">
                              {candidate.location && (
                                <div className="flex items-center gap-1 mb-1">
                                  <MapPin className="w-3 h-3 flex-shrink-0" />
                                  <span className="truncate">{candidate.location}</span>
                                </div>
                              )}
                              {candidate.expectedSalary && (
                                <div className="font-semibold text-gray-900">
                                  € {candidate.expectedSalary.toLocaleString()}
                                </div>
                              )}
                              {(candidate.spokenLanguages?.length > 0 || candidate.languages?.length > 0) && (
                                <div className="text-xs text-gray-500">
                                  {candidate.spokenLanguages?.length > 0 
                                    ? candidate.spokenLanguages.join(', ')
                                    : candidate.languages.join(', ')}
                                </div>
                              )}
                              {candidate.matchScore != null && (
                                <div className="mt-1">
                                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold ${
                                    candidate.matchScore >= 70 ? 'bg-green-100 text-green-700' :
                                    candidate.matchScore >= 40 ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-red-100 text-red-700'
                                  }`}>
                                    {candidate.matchScore}% Match
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="archive">
            <Card>
              <CardContent className="p-12 text-center">
                <p className="text-gray-600">No archived candidates</p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <AsyncInterviewModal
        open={!!asyncModalCandidate}
        onOpenChange={(o) => { if (!o) setAsyncModalCandidate(null); }}
        candidate={asyncModalCandidate}
        recruiterId={userProfile?.id}
        onCreated={() => { /* could refresh list / show toast */ }}
      />

      <BulkEmailModal
        open={!!bulkEmailType}
        onOpenChange={(o) => { if (!o) setBulkEmailType(null); }}
        type={bulkEmailType || 'contract'}
        recipients={candidates
          .filter(c => selectedIds.includes(c.applicationId))
          .filter(c => {
            // Only include candidates whose actual stage matches the email type
            const status = c.applicationStatus || c.status;
            if (bulkEmailType === 'contract') return status === 'offered' || status === 'accepted';
            if (bulkEmailType === 'rejection') return status === 'rejected';
            return true;
          })
          .map(c => ({
            application_id: c.applicationId,
            email: c.email,
            name: `${c.firstName || ''} ${c.lastName || ''}`.trim(),
            role: c.appliedPosition || c.positionTitle,
            company: c.companyName || c.positionCompany,
            recruiter: `${userProfile?.firstName || ''} ${userProfile?.lastName || ''}`.trim(),
          }))
        }
        recruiterId={userProfile?.id}
        organizationId={userProfile?.organizationId || userProfile?.organization_id}
        onSent={({ type, count, failed, failures }) => {
          const ok = count || 0;
          const fail = failed || 0;
          const baseMsg = `${type === 'contract' ? 'Contract' : 'Rejection'} email sent to ${ok} candidate${ok !== 1 ? 's' : ''}`;
          if (fail > 0) {
            const detail = (failures || []).slice(0, 3).map(f => `\n• ${f.name || f.email || '?'}: ${f.reason || 'failed'}`).join('');
            alert(`${baseMsg} (${fail} failed)${detail}`);
          } else {
            alert(baseMsg + '.');
          }
          setSelectedIds([]);
        }}
      />
    </RecruiterLayout>
  );
};

export default MyCandidates;
